import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarIcon, Loader2, CheckCircle2, Asterisk } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { SERVICE_CONFIG, SERVICE_TYPES, VEHICLE_OPTIONS } from "@/lib/bookingConfig";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import TimeSelect from "@/components/TimeSelect";
import QuoteSummary from "@/components/QuoteSummary";
import SmartVehicleSelector from "@/components/SmartVehicleSelector";
import StepProgress from "@/components/StepProgress";
import ReviewItinerary from "@/components/ReviewItinerary";
import Honeypot from "@/components/Honeypot";
import { buildQuotePayload } from "@/lib/quote";
import { detectFlight } from "@/lib/flightNumbers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayStart = () => new Date(new Date().setHours(0, 0, 0, 0));

const baseForm = {
  name: "",
  email: "",
  phone: "",
  service_type: "Airport Transfers",
  vehicle_preference: "Executive Sedan",
  notes: "",
  transfer_type: "",
  details: {},
};

export default function BookingForm({
  initialService,
  initialMode,
  initialDetails,
  initialTransferType,
  initialVehicle,
}) {
  const [tab, setTab] = useState(initialMode === "quote" ? "quote" : "booking");
  const [step, setStep] = useState(1); // 1=Trip, 2=Vehicle, 3=Review (booking tab only)
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ...baseForm,
    service_type: initialService || baseForm.service_type,
    vehicle_preference: initialVehicle || baseForm.vehicle_preference,
    details: initialDetails || {},
    transfer_type: initialTransferType || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [hpWebsite, setHpWebsite] = useState(""); // honeypot — stays empty for humans

  // Listen for vehicle prefill from fleet section
  useEffect(() => {
    const onPrefill = (e) => {
      const vehicle = e.detail;
      if (vehicle && VEHICLE_OPTIONS.includes(vehicle)) {
        setForm((f) => ({ ...f, vehicle_preference: vehicle }));
      }
    };
    window.addEventListener("sjc-prefill-vehicle", onPrefill);
    return () => window.removeEventListener("sjc-prefill-vehicle", onPrefill);
  }, []);

  // Reset details + transfer_type when service ACTUALLY changes (not on initial mount, even in StrictMode)
  const prevServiceRef = useRef(form.service_type);
  useEffect(() => {
    if (prevServiceRef.current === form.service_type) return;
    prevServiceRef.current = form.service_type;
    setForm((f) => ({ ...f, details: {}, transfer_type: "" }));
  }, [form.service_type]);

  // Airport Transfers: auto-detect airline + flight type from flight number(s).
  // Handles single flight_number (Arrival/Departure) and both arrival_flight_number
  // + return_flight_number (Return-Both).
  const flightNumberValue = form.details.flight_number;
  const arrivalFlightNumberValue = form.details.arrival_flight_number;
  const returnFlightNumberValue = form.details.return_flight_number;
  useEffect(() => {
    if (form.service_type !== "Airport Transfers") return;
    setForm((f) => {
      const next = { ...f.details };

      // Single flight (Arrival or Departure transfer type)
      if (flightNumberValue !== undefined) {
        const info = detectFlight(flightNumberValue || "");
        if (info) {
          next.airline = info.airline;
          next.airline_auto = info.airline;
          if (info.flightType) {
            next.flight_type = info.flightType;
            next.flight_type_auto = info.flightType;
          } else {
            next.flight_type_auto = null;
          }
        } else {
          next.airline_auto = null;
          next.flight_type_auto = null;
        }
      }

      // Return-Both: arrival leg
      if (arrivalFlightNumberValue !== undefined) {
        const info = detectFlight(arrivalFlightNumberValue || "");
        if (info) {
          next.arrival_airline = info.airline;
          next.arrival_airline_auto = info.airline;
          if (info.flightType) next.flight_type_auto = info.flightType;
        } else {
          next.arrival_airline_auto = null;
        }
      }

      // Return-Both: return leg
      if (returnFlightNumberValue !== undefined) {
        const info = detectFlight(returnFlightNumberValue || "");
        if (info) {
          next.return_airline = info.airline;
          next.return_airline_auto = info.airline;
        } else {
          next.return_airline_auto = null;
        }
      }

      return { ...f, details: next };
    });
  }, [flightNumberValue, arrivalFlightNumberValue, returnFlightNumberValue, form.service_type]);

  // Airport Transfers: auto-calculate Number of Children = Total Passengers - Number of Adults
  const passengersVal = form.details.passengers;
  const adultsVal = form.details.adults;
  useEffect(() => {
    if (form.service_type !== "Airport Transfers") return;
    const p = Number(passengersVal);
    const a = Number(adultsVal);
    const hasBoth = !Number.isNaN(p) && !Number.isNaN(a) && p > 0 && a >= 1;
    const computed = hasBoth ? Math.max(0, p - a) : null;
    setForm((f) => {
      if (f.details.children === computed) return f;
      const next = { ...f.details, children: computed };
      // If children went to 0/null, clear child-seat fields so hidden data doesn't linger
      if (!computed || computed <= 0) {
        delete next.children_under_6;
        delete next.ages_under_6;
      }
      return { ...f, details: next };
    });
  }, [passengersVal, adultsVal, form.service_type]);

  // When passengers moves ACROSS the auto/compare boundary, reset the vehicle
  // to the default compare pick. This lives here (not in SmartVehicleSelector)
  // because the selector remounts on each step-2 entry, losing its own refs.
  // - 4/5+ pax : auto-selects Luxury SUV / Luxury Van (handled by the selector)
  // - 1-3 pax : must restore Executive Sedan (default compare) if user was
  //             previously auto-locked into SUV/Van.
  const prevPaxRef = useRef(null);
  useEffect(() => {
    const p = Number(passengersVal) || 0;
    const prev = prevPaxRef.current;
    prevPaxRef.current = p;
    if (prev === null) return; // don't touch on initial mount (URL preselect)
    const wasAuto = prev >= 4;
    const nowCompare = p >= 1 && p <= 3;
    if (wasAuto && nowCompare) {
      setForm((f) => ({ ...f, vehicle_preference: "Executive Sedan" }));
    }
  }, [passengersVal]);

  const svc = SERVICE_CONFIG[form.service_type];
  const needsTransfer = !!svc?.transferType;

  const activeSchema = useMemo(() => {
    if (!svc) return null;
    if (needsTransfer) {
      if (!form.transfer_type) return null;
      return svc.byTransfer?.[form.transfer_type] || null;
    }
    return svc;
  }, [svc, needsTransfer, form.transfer_type]);

  const visibleFields = useMemo(() => {
    if (!activeSchema) return [];
    const all = activeSchema.groups
      ? activeSchema.groups.flatMap((g) => g.fields)
      : activeSchema.fields || [];
    return all.filter((f) => !f.showIf || f.showIf(form.details));
  }, [activeSchema, form.details]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateDetail = (k, v) =>
    setForm((f) => ({ ...f, details: { ...f.details, [k]: v } }));

  // Live quote payload (booking mode only) - derived from the current form.
  const quotePayload = useMemo(() => {
    if (tab !== "booking") return null;
    return buildQuotePayload({
      vehicleName: form.vehicle_preference,
      details: { ...form.details, transfer_type: form.transfer_type },
    });
  }, [tab, form.vehicle_preference, form.details, form.transfer_type]);

  const validate = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return "Please enter a valid email address.";
    if (!form.phone.trim()) return "Please enter your phone number.";
    if (!form.service_type) return "Please choose a service type.";
    if (needsTransfer && !form.transfer_type)
      return `Please select a transfer type for ${form.service_type}.`;

    for (const f of visibleFields) {
      if (!f.required) continue;
      const val = form.details[f.name];
      if (val === undefined || val === null || val === "") {
        return `Please fill in: ${f.label}`;
      }
    }
    return null;
  };

  const buildCheckoutParams = (details) => {
    const p = new URLSearchParams();
    p.set("service", form.service_type);
    if (form.vehicle_preference) p.set("vehicle", form.vehicle_preference);
    if (details.passengers !== undefined) p.set("passengers", String(details.passengers));
    if (details.luggage_count !== undefined) p.set("luggage", String(details.luggage_count));

    const date =
      details.pickup_date ||
      details.arrival_date ||
      details.departure_date ||
      details.cruise_date ||
      details.ceremony_date ||
      details.event_date;
    if (date) p.set("date", date);

    const time =
      details.pickup_time ||
      details.arrival_time ||
      details.arrival_pickup_time ||
      details.departure_time ||
      details.departure_pickup_time ||
      details.cruise_time ||
      details.ceremony_time ||
      details.event_start_time;
    if (time) p.set("time", time);

    const pickup =
      details.pickup_location ||
      details.pickup_address ||
      details.arrival_airport;
    if (pickup) p.set("pickup", pickup);

    const destination =
      details.destination ||
      details.dropoff_address ||
      details.arrival_dropoff_address ||
      details.destination_airport ||
      details.wedding_venue ||
      details.event_location ||
      details.cruise_terminal;
    if (destination) p.set("destination", destination);

    return p.toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    // Build details from visible fields
    const details = {};
    visibleFields.forEach((f) => {
      const v = form.details[f.name];
      if (v !== undefined && v !== "" && v !== null) {
        details[f.name] = v;
      }
    });
    if (needsTransfer) details.transfer_type = form.transfer_type;

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      service_type: form.service_type,
      vehicle_preference: form.vehicle_preference,
      notes: form.notes.trim() || null,
      request_type: tab,
      details,
      website: hpWebsite,
    };

    // BOOKING MODE: proceed to checkout (payment step) instead of submitting now.
    if (tab === "booking") {
      try {
        sessionStorage.setItem("sjc_pending_booking", JSON.stringify(payload));
      } catch (_) {
        // ignore quota errors; checkout will still work from URL params
      }
      navigate(`/checkout?${buildCheckoutParams(details)}`);
      return;
    }

    // QUOTE MODE: submit directly as before.
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, payload);
      setSuccess({ id: res.data.id, type: tab, message: res.data.message });
      toast.success("Quote request submitted");
      setForm(baseForm);
    } catch (e2) {
      const msg =
        e2?.response?.data?.detail ||
        "Something went wrong. Please try again or call us at +61 400 000 000.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section id="book" data-testid="booking-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Card data-testid="booking-success" className="p-10 md:p-14 text-center border-charcoal/10 rounded-none">
            <div className="w-14 h-14 rounded-full border border-gold/40 text-gold grid place-items-center mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2
              className="mt-5 text-2xl md:text-3xl font-semibold text-zinc-900"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {success.type === "quote" ? "Quote request received" : "Booking request received"}
            </h2>
            <p className="mt-3 text-zinc-600">{success.message}</p>
            <p className="mt-2 text-xs text-zinc-400">
              Reference ID:{" "}
              <span data-testid="booking-success-id" className="font-mono">
                {success.id}
              </span>
            </p>
            <Button
              data-testid="booking-new-btn"
              onClick={() => setSuccess(null)}
              className="mt-8 bg-midnight hover:bg-gold text-warm-white hover:text-midnight rounded-none px-8 py-6 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500"
            >
              Submit another request
            </Button>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="book" data-testid="booking-section" className="py-16 md:py-24 bg-ivory">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="sjc-eyebrow text-muted-light flex items-center gap-3">
            <span className="sjc-hairline w-8" />
            {tab === "quote" ? "Request a Quote" : "Reserve Your Journey"}
          </p>
          <h2
            data-testid="booking-heading"
            className="font-display mt-5 text-3xl md:text-4xl lg:text-[46px] leading-[1.1] tracking-[-0.015em] text-charcoal"
          >
            Tell us about <span className="italic font-light">your trip.</span>
          </h2>
          <p className="mt-4 text-base text-muted-light font-light leading-relaxed">
            Pick your service below and we&apos;ll show only the fields that matter. Required fields
            are marked with <span className="text-gold font-medium">*</span>.
          </p>
        </div>

        <div className="mt-12 md:mt-14 bg-white border border-charcoal/10 p-6 md:p-10 lg:p-12 rounded-none">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList
              data-testid="booking-tabs"
              className="grid grid-cols-2 w-full sm:w-72 mb-10 bg-ivory p-1 border border-charcoal/10 rounded-none h-auto"
            >
              <TabsTrigger
                data-testid="tab-booking"
                value="booking"
                className="text-[11px] tracking-[0.22em] uppercase font-medium data-[state=active]:bg-midnight data-[state=active]:text-warm-white data-[state=active]:shadow-none rounded-none py-2.5"
              >
                Book Now
              </TabsTrigger>
              <TabsTrigger
                data-testid="tab-quote"
                value="quote"
                className="text-[11px] tracking-[0.22em] uppercase font-medium data-[state=active]:bg-midnight data-[state=active]:text-warm-white data-[state=active]:shadow-none rounded-none py-2.5"
              >
                Get a Quote
              </TabsTrigger>
            </TabsList>

            <TabsContent value={tab} forceMount>
              <form onSubmit={handleSubmit} className="space-y-8">
                <Honeypot value={hpWebsite} onChange={setHpWebsite} />
                {tab === "booking" && (
                  <div data-testid="booking-progress" className="pb-2">
                    <StepProgress current={step} onStepClick={(n) => n < step && setStep(n)} />
                  </div>
                )}
                <div className={tab === "booking" && step !== 1 ? "hidden" : "space-y-8"}>
                {/* Standard top fields */}
                <FieldGrid>
                  <FieldWrap label="Full Name" required>
                    <Input
                      data-testid="booking-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="John Smith"
                    />
                  </FieldWrap>
                  <FieldWrap label="Phone Number" required>
                    <Input
                      data-testid="booking-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+61 400 123 456"
                    />
                  </FieldWrap>
                  <FieldWrap label="Email Address" required span={2}>
                    <Input
                      data-testid="booking-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                    />
                  </FieldWrap>

                  <FieldWrap label="Service Type" required>
                    <Select
                      value={form.service_type}
                      onValueChange={(v) => update("service_type", v)}
                    >
                      <SelectTrigger
                        data-testid="booking-service-type"
                        className="border-charcoal/15 rounded-none h-11 focus:border-midnight focus:ring-0"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((s) => (
                          <SelectItem
                            key={s}
                            value={s}
                            data-testid={`service-option-${slug(s)}`}
                          >
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldWrap>
                </FieldGrid>

                {/* Transfer Type for Airport / Cruise */}
                <AnimatePresence mode="wait">
                  {needsTransfer && (
                    <motion.div
                      key={`tt-${form.service_type}`}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.25 }}
                      className="border-t border-charcoal/10 pt-6"
                    >
                      <SectionTitle>Transfer Type</SectionTitle>
                      <div
                        data-testid="transfer-type-options"
                        className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3"
                      >
                        {svc.transferType.options.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`transfer-option-${slug(opt)}`}
                            onClick={() => update("transfer_type", opt)}
                            className={cn(
                              "px-4 py-3 rounded-lg border text-sm font-medium transition-all text-left",
                              form.transfer_type === opt
                                ? "border-midnight bg-midnight text-warm-white"
                                : "border-charcoal/15 text-charcoal hover:border-midnight hover:bg-ivory",
                            )}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Dynamic service-specific section */}
                <AnimatePresence mode="wait">
                  {activeSchema && (
                    <motion.div
                      key={`fields-${form.service_type}-${form.transfer_type || "x"}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-charcoal/10 pt-6 space-y-6"
                    >
                      {activeSchema.groups ? (
                        activeSchema.groups.map((g) => (
                          <div key={g.title}>
                            <SectionTitle>{g.title}</SectionTitle>
                            <FieldGrid className="mt-3">
                              {g.fields
                                .filter((f) => !f.showIf || f.showIf(form.details))
                                .map((f) => (
                                  <DynamicField
                                    key={f.name}
                                    field={f}
                                    value={form.details[f.name]}
                                    onChange={(v) => updateDetail(f.name, v)}
                                  />
                                ))}
                            </FieldGrid>
                          </div>
                        ))
                      ) : (
                        <div>
                          <SectionTitle>{form.service_type} Details</SectionTitle>
                          <FieldGrid className="mt-3">
                            {activeSchema.fields
                              .filter((f) => !f.showIf || f.showIf(form.details))
                              .map((f) => (
                                <DynamicField
                                  key={f.name}
                                  field={f}
                                  value={form.details[f.name]}
                                  onChange={(v) => updateDetail(f.name, v)}
                                />
                              ))}
                          </FieldGrid>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Notes */}
                <div className="border-t border-charcoal/10 pt-6">
                  <FieldGrid>
                    <FieldWrap label="Additional Notes" span={2}>
                      <Textarea
                        data-testid="booking-notes"
                        rows={4}
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                        placeholder="Anything else we should know? Special requests, accessibility needs, etc."
                      />
                    </FieldWrap>
                  </FieldGrid>
                </div>

                </div>
                {tab === "booking" && step === 2 && (
                  <div className="pt-2 space-y-6">
                    <SmartVehicleSelector
                      vehicleName={form.vehicle_preference}
                      onSelect={(name) => update("vehicle_preference", name)}
                      details={{ ...form.details, transfer_type: form.transfer_type }}
                    />
                  </div>
                )}
                {tab === "booking" && step === 3 && (
                  <div data-testid="booking-review" className="pt-2">
                    <ReviewItinerary
                      serviceType={form.service_type}
                      transferType={form.transfer_type}
                      details={form.details}
                      vehicleName={form.vehicle_preference}
                      quotePayload={quotePayload}
                      onEditTrip={() => setStep(1)}
                      onEditVehicle={() => setStep(2)}
                    />
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                  {tab === "booking" && step > 1 && (
                    <Button
                      type="button"
                      data-testid="wizard-back-btn"
                      variant="outline"
                      onClick={() => setStep((s) => Math.max(1, s - 1))}
                      className="w-full sm:w-auto border-charcoal/20 text-muted-light hover:text-charcoal hover:bg-ivory hover:border-charcoal/40 px-7 rounded-none bg-transparent"
                    >
                      Back
                    </Button>
                  )}
                  <p className="text-xs text-zinc-500 flex-1 hidden sm:block">
                    By submitting, you agree to be contacted about your request.
                  </p>
                  {tab === "booking" && step < 3 ? (
                    <Button
                      key="wizard-next"
                      type="button"
                      data-testid="wizard-next-btn"
                      onClick={() => {
                        if (step === 1) {
                          const err = validate();
                          if (err) return toast.error(err);
                        }
                        setStep((s) => Math.min(3, s + 1));
                        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
                      }}
                      className="w-full sm:w-auto bg-midnight hover:bg-gold text-warm-white hover:text-midnight font-semibold px-9 py-6 rounded-none text-[11px] tracking-[0.28em] uppercase transition-all duration-500"
                    >
                      {step === 1 ? "Continue to Vehicle" : "Review Booking"}
                    </Button>
                  ) : (
                    <Button
                      key="wizard-submit"
                      data-testid="booking-submit-btn"
                      type="submit"
                      disabled={submitting}
                      className="w-full sm:w-auto bg-midnight hover:bg-gold text-warm-white hover:text-midnight font-semibold px-9 py-6 rounded-none text-[11px] tracking-[0.28em] uppercase transition-all duration-500"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : tab === "quote" ? (
                        "Request Quote"
                      ) : (
                        "Continue to Payment"
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}

/* ---------- Layout helpers ---------- */
function FieldGrid({ children, className = "" }) {
  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-5", className)}>{children}</div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="sjc-eyebrow text-muted-light flex items-center gap-3">
      <span className="sjc-hairline w-6" />
      {children}
    </h3>
  );
}

function FieldWrap({ label, required, span = 1, children, testid }) {
  return (
    <div
      data-testid={testid}
      className={cn(span === 2 && "md:col-span-2")}
    >
      <Label className="text-[11px] tracking-[0.22em] uppercase font-medium text-muted-light inline-flex items-center gap-1">
        {label}
        {required && <span className="text-gold text-sm leading-none">*</span>}
      </Label>
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

/* ---------- Dynamic field renderer ---------- */
function DynamicField({ field, value, onChange }) {
  const span = field.span || 1;
  const testid = `field-${field.name.replace(/_/g, "-")}`;
  const placeholder = field.placeholder;
  const fieldId = `f-${field.name}`;

  let control = null;
  switch (field.type) {
    case "text":
    case "email":
    case "tel":
      control = (
        <Input
          id={fieldId}
          data-testid={testid}
          type={field.type === "tel" ? "tel" : field.type === "email" ? "email" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      );
      break;

    case "address":
      control = (
        <AddressAutocomplete
          testid={testid}
          value={value ?? ""}
          onChange={(v) => onChange(v)}
          placeholder={placeholder}
        />
      );
      break;

    case "number":
      control = (
        <Input
          id={fieldId}
          data-testid={testid}
          type="number"
          min={field.min}
          max={field.max}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          placeholder={placeholder}
        />
      );
      break;

    case "time":
      control = (
        <TimeSelect
          testid={testid}
          value={value ?? ""}
          onChange={(v) => onChange(v)}
          placeholder="Select time"
        />
      );
      break;

    case "textarea":
      control = (
        <Textarea
          id={fieldId}
          data-testid={testid}
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      );
      break;

    case "date": {
      control = <DatePickerField testid={testid} value={value} onChange={onChange} />;
      break;
    }

    case "select":
      control = (
        <Select value={value ?? ""} onValueChange={(v) => onChange(v)}>
          <SelectTrigger data-testid={testid} className="border-charcoal/15 rounded-none h-11 focus:border-midnight focus:ring-0">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((o) => (
              <SelectItem key={o} value={o} data-testid={`${testid}-opt-${slug(o)}`}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;

    case "yesno":
      control = (
        <div data-testid={testid} className="flex gap-2">
          {["Yes", "No"].map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                data-testid={`${testid}-${opt.toLowerCase()}`}
                onClick={() => onChange(opt)}
                className={cn(
                  "flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors",
                  selected
                    ? "border-midnight bg-midnight text-warm-white"
                    : "border-charcoal/15 text-charcoal hover:border-midnight hover:bg-ivory",
                )}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
      break;

    case "auto_badge": {
      // Flight-type badges read as "International Flight" / "Domestic Flight".
      // Airline badges just render the airline name.
      const isFlightType = field.name === "flight_type" || field.name === "arrival_flight_type" || field.name === "return_flight_type";
      const display = value ? (isFlightType ? `${value} Flight` : value) : "Detected";
      control = (
        <div
          data-testid={testid}
          className="inline-flex items-center gap-3 px-4 py-3 bg-ivory border border-gold/25 text-sm font-medium text-charcoal"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          <span>{display}</span>
          <span className="text-[10px] tracking-[0.22em] uppercase text-muted-light/70 font-medium">
            auto-detected
          </span>
        </div>
      );
      break;
    }

    case "computed":
      control = (
        <div
          data-testid={testid}
          className="h-11 px-3.5 border border-charcoal/15 bg-ivory text-sm text-charcoal font-medium flex items-center justify-between"
        >
          <span>{value ?? 0}</span>
          <span className="text-[10px] font-medium tracking-[0.22em] uppercase text-muted-light/70">
            Auto
          </span>
        </div>
      );
      break;

    default:
      control = null;
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.22 }}
      className={cn(span === 2 && "md:col-span-2")}
    >
      <Label htmlFor={fieldId} className="text-[11px] tracking-[0.22em] uppercase font-medium text-muted-light inline-flex items-center gap-1">
        {field.label}
        {field.required && <span className="text-gold text-sm leading-none">*</span>}
      </Label>
      {field.helper && (
        <p
          data-testid={`${testid}-helper`}
          className="mt-1.5 text-xs text-muted-light/80 leading-relaxed font-light italic"
          style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "13px" }}
        >
          {field.helper}
        </p>
      )}
      <div className="mt-2.5">{control}</div>
    </motion.div>
  );
}

/* ---------- Date picker (auto-closes on selection) ---------- */
function DatePickerField({ testid, value, onChange }) {
  const [open, setOpen] = useState(false);
  const parsed = value ? new Date(value) : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          data-testid={`${testid}-trigger`}
          variant="outline"
          className={cn(
            "w-full justify-start font-normal border-charcoal/15 rounded-none h-11 bg-white hover:bg-ivory",
            !value && "text-zinc-400",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {parsed ? format(parsed, "PPP") : "Pick a date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsed || undefined}
          onSelect={(d) => {
            onChange(d ? format(d, "yyyy-MM-dd") : "");
            if (d) setOpen(false);
          }}
          disabled={(d) => d < todayStart()}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function slug(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
